import type { ReactNode } from "react";
import { Bg, Cloud, Coin, CoinStack, Cube, Gem, L0, L1, L2, Rays, Sparkle, Star5, Streaks, type SceneProps } from "./prims";

const pts = (a: [number, number][]) => a.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");

/* ─────────────── Skak: a board in perspective with king, queen and pawns ─────────────── */
function Piece({ u, kind, x, y, s, light }: { u: string; kind: "king" | "queen" | "pawn"; x: number; y: number; s: number; light: boolean }) {
  const fill = `url(#${u}${light ? "pw" : "pd"})`;
  const line = light ? "#8d9bc6" : "#0d1340";
  const body = kind === "pawn"
    ? "M-30 0 Q-30 -12 -20 -14 Q-12 -18 -12 -32 Q-22 -34 -22 -42 H22 Q22 -34 12 -32 Q12 -18 20 -14 Q30 -12 30 0Z"
    : "M-38 0 Q-38 -14 -25 -18 Q-14 -24 -14 -44 Q-21 -92 -16 -106 H16 Q21 -92 14 -44 Q14 -24 25 -18 Q38 -14 38 0Z";
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`} stroke={line} strokeWidth={2.2} strokeLinejoin="round">
      <ellipse cx={4} cy={3} rx={44} ry={9} fill="#000" opacity={0.28} stroke="none" />
      <path d={body} fill={fill} />
      {kind === "pawn" ? (
        <>
          <rect x={-25} y={-48} width={50} height={9} rx={4.5} fill={fill} />
          <circle cy={-66} r={18} fill={fill} />
          <path d="M-10 -73 A11 11 0 0 1 -2 -80" fill="none" stroke="#fff" strokeOpacity={light ? 0.95 : 0.4} strokeWidth={4} strokeLinecap="round" />
        </>
      ) : (
        <>
          <rect x={-26} y={-114} width={52} height={11} rx={5.5} fill={fill} />
          {kind === "king" ? (
            <>
              <path d="M-19 -113 Q-27 -146 0 -148 Q27 -146 19 -113Z" fill={fill} />
              <rect x={-5} y={-182} width={10} height={36} rx={4} fill="#ffc61a" stroke="#d98200" />
              <rect x={-14} y={-170} width={28} height={10} rx={4} fill="#ffc61a" stroke="#d98200" />
            </>
          ) : (
            <>
              <path d="M-22 -113 L-30 -150 L-15 -134 L-9 -158 L0 -136 L9 -158 L15 -134 L30 -150 L22 -113Z" fill={fill} />
              {[[-30, -153], [-9, -162], [9, -162], [30, -153]].map(([a, b], i) => <circle key={i} cx={a} cy={b} r={5.5} fill="#ffc61a" stroke="#d98200" />)}
            </>
          )}
          <path d="M-9 -96 Q-12 -70 -9 -48" fill="none" stroke="#fff" strokeOpacity={light ? 0.95 : 0.35} strokeWidth={4} strokeLinecap="round" />
        </>
      )}
      <path d="M-21 -8 Q-8 -12 6 -8" fill="none" stroke="#fff" strokeOpacity={light ? 0.9 : 0.3} strokeWidth={3} strokeLinecap="round" />
    </g>
  );
}
export function Skak({ u }: SceneProps) {
  const Y0 = 146, Y1 = 338;
  const yAt = (j: number) => Y0 + (Y1 - Y0) * Math.pow(j / 8, 1.35);
  const wAt = (y: number) => 250 + 340 * ((y - Y0) / (Y1 - Y0));
  const X = (i: number, y: number) => 320 + (i / 8 - 0.5) * wAt(y);
  const cell = (i: number, j: number): [number, number][] => [[X(i, yAt(j)), yAt(j)], [X(i + 1, yAt(j)), yAt(j)], [X(i + 1, yAt(j + 1)), yAt(j + 1)], [X(i, yAt(j + 1)), yAt(j + 1)]];
  const sq: ReactNode[] = [];
  for (let j = 0; j < 8; j++) for (let i = 0; i < 8; i++) sq.push(<polygon key={`${i}-${j}`} points={pts(cell(i, j))} fill={(i + j) % 2 ? "#2aa7c9" : "#fff4d6"} />);
  const spot = (i: number, j: number): [number, number, number] => { const yb = yAt(j + 1) - (yAt(j + 1) - yAt(j)) * 0.18; return [X(i + 0.5, yb), yb, wAt(yb) / 590]; };
  const place = (i: number, j: number, kind: "king" | "queen" | "pawn", light: boolean, k = 1) => { const [x, y, sc] = spot(i, j); return <Piece key={`${i}${j}`} u={u} kind={kind} x={x} y={y} s={sc * k} light={light} />; };
  const dots: ReactNode[] = [[2, 5], [3, 5], [4, 5], [2, 6], [4, 6]].map(([i, j]) => { const [x, y, sc] = spot(i!, j!); return <ellipse key={`${i}${j}`} cx={x} cy={y - 10 * sc} rx={14 * sc} ry={7 * sc} fill="#3be28a" stroke="#fff" strokeWidth={2.5} opacity={0.95} />; });
  return (
    <>
      <defs>
        <linearGradient id={`${u}pw`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#ffffff" /><stop offset="0.55" stopColor="#e6ecfb" /><stop offset="1" stopColor="#a4b2da" /></linearGradient>
        <linearGradient id={`${u}pd`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#6678c4" /><stop offset="0.5" stopColor="#2e3c82" /><stop offset="1" stopColor="#141c4c" /></linearGradient>
      </defs>
      <Bg u={u} a="#7a5cff" b="#1c2f9a" glow={0.55} cy={40} />
      <L0>
        <Rays cx={320} cy={110} n={22} len={560} color="#ffe27a" opacity={0.17} />
        {[[70, 60], [566, 54], [112, 120], [534, 112]].map(([x, y], i) => <Sparkle key={i} x={x!} y={y!} s={i % 2 ? 9 : 13} />)}
      </L0>
      <L1>
        <polygon points={pts([[X(0, Y0) - 8, Y0 - 8], [X(8, Y0) + 8, Y0 - 8], [X(8, Y1) + 16, Y1 + 12], [X(0, Y1) - 16, Y1 + 12]])} fill="#3a2a8c" />
        <polygon points={pts([[X(0, Y0) - 5, Y0 - 5], [X(8, Y0) + 5, Y0 - 5], [X(8, Y1) + 10, Y1 + 8], [X(0, Y1) - 10, Y1 + 8]])} fill="#ffc61a" />
        {sq}
        <polygon points={pts(cell(3, 6))} fill="#ffe27a" opacity={0.75} stroke="#fff" strokeWidth={3} />
        {dots}
        {place(6, 3, "pawn", false)}{place(2, 3, "pawn", false)}{place(5, 4, "queen", false, 1)}
        {place(7, 6, "pawn", true)}{place(1, 6, "pawn", true)}{place(3, 6, "king", true, 1)}
      </L1>
      <L2>
        <g transform="translate(78 78) rotate(-12)">
          <path d="M-30 14 L-36 -22 L-16 -4 L0 -30 L16 -4 L36 -22 L30 14Z" fill="#ffc61a" stroke="#d98200" strokeWidth={4} strokeLinejoin="round" />
          <rect x={-30} y={14} width={60} height={11} rx={5} fill="#ffa600" stroke="#d98200" strokeWidth={3} />
          {[-36, 0, 36].map((x, i) => <circle key={i} cx={x} cy={i === 1 ? -33 : -25} r={5} fill="#ff4d6d" stroke="#fff" strokeWidth={2} />)}
        </g>
        <Star5 x={564} y={96} r={14} fill="#fff6bd" stroke="#ffb000" />
        <Sparkle x={470} y={60} s={11} fill="#ffe27a" /><Sparkle x={150} y={260} s={9} />
      </L2>
    </>
  );
}

/* ─────────────── Kryds og bolle: a glossy 3×3 board with a golden winning diagonal ─────────────── */
const Xmark = ({ x, y, s = 1 }: { x: number; y: number; s?: number }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    {[45, -45].map((r) => (
      <g key={r} transform={`rotate(${r})`}>
        <rect x={-34} y={-8} width={68} height={18} rx={9} fill="#c42040" />
        <rect x={-34} y={-11} width={68} height={18} rx={9} fill="#ff4d6d" />
        <rect x={-28} y={-8} width={38} height={4.5} rx={2.2} fill="#fff" opacity={0.55} />
      </g>
    ))}
  </g>
);
const Omark = ({ x, y, s = 1 }: { x: number; y: number; s?: number }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <circle r={26} cy={3} fill="none" stroke="#0f4fa8" strokeWidth={17} />
    <circle r={26} fill="none" stroke="#2f8dff" strokeWidth={17} />
    <path d="M-26 -8 A26 26 0 0 1 -6 -25" fill="none" stroke="#fff" strokeOpacity={0.7} strokeWidth={4.5} strokeLinecap="round" />
  </g>
);
export function Kryds({ u }: SceneProps) {
  const x0 = 182, y0 = 52, c = 92, cx = (k: number) => x0 + c * k + c / 2, cy = (k: number) => y0 + c * k + c / 2;
  const marks: [number, number, "x" | "o"][] = [[0, 0, "x"], [2, 0, "o"], [0, 1, "o"], [1, 1, "x"], [1, 2, "o"], [2, 2, "x"], [2, 1, "o"]];
  return (
    <>
      <Bg u={u} a="#43e0c0" b="#1b6fd0" glow={0.6} />
      <L0>
        <Rays cx={320} cy={190} n={18} len={560} opacity={0.14} />
        <g opacity={0.35}>
          <Xmark x={78} y={84} s={0.8} /><Omark x={568} y={86} s={0.8} /><Omark x={92} y={282} s={0.6} /><Xmark x={560} y={280} s={0.7} />
        </g>
      </L0>
      <L1>
        <rect x={x0 - 14} y={y0 - 6} width={c * 3 + 28} height={c * 3 + 28} rx={34} fill="#0a3f8f" opacity={0.55} />
        <rect x={x0 - 14} y={y0 - 14} width={c * 3 + 28} height={c * 3 + 28} rx={34} fill="#fff" stroke="#bfe0ff" strokeWidth={5} />
        <rect x={x0 - 4} y={y0 - 4} width={c * 3 + 8} height={c * 3 + 8} rx={26} fill="#eef6ff" />
        {[1, 2].map((k) => <rect key={`v${k}`} x={x0 + c * k - 4} y={y0 + 6} width={8} height={c * 3 - 12} rx={4} fill="#a9c4f2" />)}
        {[1, 2].map((k) => <rect key={`h${k}`} x={x0 + 6} y={y0 + c * k - 4} width={c * 3 - 12} height={8} rx={4} fill="#a9c4f2" />)}
        {marks.map(([a, b, t], i) => t === "x" ? <Xmark key={i} x={cx(a)} y={cy(b)} /> : <Omark key={i} x={cx(a)} y={cy(b)} />)}
        <line x1={cx(0)} y1={cy(0)} x2={cx(2)} y2={cy(2)} stroke="#fff" strokeWidth={22} strokeLinecap="round" opacity={0.9} />
        <line x1={cx(0)} y1={cy(0)} x2={cx(2)} y2={cy(2)} stroke="#ffb300" strokeWidth={11} strokeLinecap="round" />
        <path d="M182 40 Q320 12 458 40" fill="none" stroke="#fff" strokeOpacity={0.0} />
      </L1>
      <L2>
        <g transform="translate(560 296) rotate(8) scale(0.9)">
          <path d="M-36 -30 H36 V-6 Q36 28 0 34 Q-36 28 -36 -6Z" fill="#ffc61a" stroke="#d98200" strokeWidth={4} strokeLinejoin="round" />
          <path d="M-36 -22 Q-58 -22 -52 2 Q-48 14 -32 12 M36 -22 Q58 -22 52 2 Q48 14 32 12" fill="none" stroke="#d98200" strokeWidth={5} strokeLinecap="round" />
          <rect x={-8} y={34} width={16} height={12} fill="#e29a00" /><rect x={-24} y={44} width={48} height={11} rx={4} fill="#ffa600" stroke="#d98200" strokeWidth={3} />
          <Star5 x={0} y={-6} r={14} fill="#fff6bd" stroke="#e08e00" />
        </g>
        <Star5 x={110} y={168} r={18} fill="#fff6bd" stroke="#ffb000" /><Star5 x={540} y={150} r={13} fill="#fff6bd" stroke="#ffb000" />
        <Sparkle x={86} y={300} s={12} /><Sparkle x={560} y={44} s={11} /><Sparkle x={150} y={40} s={9} />
        <Coin x={96} y={240} r={15} rot={-16} /><Coin x={534} y={206} r={13} rot={12} />
      </L2>
    </>
  );
}

/* ─────────────── Quizduel: two players, a question and a VS badge ─────────────── */
function Face({ x, y, skin, hair, shirt, dir, ponytail, brow = 10 }: { x: number; y: number; skin: string; hair: string; shirt: string; dir: 1 | -1; ponytail?: boolean; brow?: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <path d="M-100 160 Q-100 76 -42 66 H42 Q100 76 100 160Z" fill={shirt} />
      <path d="M-26 66 L0 98 L26 66Z" fill="#fff" opacity={0.9} />
      <rect x={-16} y={42} width={32} height={34} rx={10} fill={skin} />
      {ponytail && <g><circle cx={-dir * 72} cy={-42} r={24} fill={hair} /><circle cx={-dir * 80} cy={-34} r={10} fill="#fff" opacity={0.16} /></g>}
      <circle cx={-62} cy={8} r={11} fill={skin} /><circle cx={62} cy={8} r={11} fill={skin} />
      <circle r={62} fill={skin} />
      <path d="M-64 -4 Q-74 -74 0 -76 Q74 -74 64 -4 Q56 -44 4 -44 Q-30 -40 -46 -10 Q-52 -2 -64 -4Z" fill={hair} />
      <path d="M-40 -56 Q-18 -70 6 -66" fill="none" stroke="#fff" strokeOpacity={0.3} strokeWidth={5} strokeLinecap="round" />
      {[-22, 22].map((ex, i) => (
        <g key={i}>
          <ellipse cx={ex} cy={6} rx={12} ry={14} fill="#fff" />
          <circle cx={ex + dir * 4.5} cy={8} r={7} fill="#20244d" /><circle cx={ex + dir * 6.5} cy={5} r={2.4} fill="#fff" />
          <path d={`M${ex - 14} ${-14 + (i === 0 ? brow * (dir === 1 ? 0.0 : 0.5) : brow * (dir === 1 ? 0.5 : 0.0))} L${ex + 14} ${-14 + (i === 0 ? brow * (dir === 1 ? 0.5 : 0.0) : brow * (dir === 1 ? 0.0 : 0.5))}`} stroke={hair} strokeWidth={5.5} strokeLinecap="round" />
        </g>
      ))}
      <circle cx={-38} cy={28} r={9} fill="#ff6f91" opacity={0.45} /><circle cx={38} cy={28} r={9} fill="#ff6f91" opacity={0.45} />
      <path d="M-18 30 Q0 54 18 30 Q0 37 -18 30Z" fill="#7a1f2b" stroke="#7a1f2b" strokeWidth={3} strokeLinejoin="round" />
      <path d="M-10 36 Q0 44 10 36 Q0 40 -10 36Z" fill="#ff8aa0" />
    </g>
  );
}
export function Quizduel({ u }: SceneProps) {
  return (
    <>
      <Bg u={u} a="#4f8bff" b="#2146c4" glow={0.4} />
      <polygon points="372,0 640,0 640,360 252,360" fill="#ff5c9a" />
      <polygon points="372,0 640,0 640,360 252,360" fill={`url(#${u}gl)`} opacity={0.5} />
      <L0>
        <polygon points="364,0 382,0 262,360 244,360" fill="#fff" opacity={0.9} />
        {[[60, 60], [584, 62], [120, 330], [520, 330]].map(([x, y], i) => <Sparkle key={i} x={x!} y={y!} s={i % 2 ? 9 : 13} o={0.9} />)}
      </L0>
      <L1>
        <Face x={140} y={206} skin="#ffd2a8" hair="#4a2a1a" shirt="#1f3fa8" dir={1} />
        <Face x={500} y={206} skin="#c98b5e" hair="#2a1530" shirt="#c21e68" dir={-1} ponytail />
      </L1>
      <L2>
        {/* the question */}
        <g transform="translate(320 70)">
          <rect x={-112} y={-42} width={224} height={92} rx={24} fill="#0a1a66" opacity={0.28} transform="translate(0 7)" />
          <rect x={-112} y={-42} width={224} height={92} rx={24} fill="#fff" stroke="#ffd23f" strokeWidth={6} />
          <text x={-68} y={22} textAnchor="middle" fontSize={64} fill="#ff4d6d" style={{ fontFamily: "var(--display)" }}>?</text>
          <rect x={-30} y={-18} width={110} height={14} rx={7} fill="#c6d4f6" /><rect x={-30} y={6} width={78} height={14} rx={7} fill="#dde6fa" />
          <path d="M-14 50 L0 70 L14 50Z" fill="#fff" stroke="#ffd23f" strokeWidth={6} strokeLinejoin="round" />
          <rect x={-12} y={46} width={24} height={8} fill="#fff" />
        </g>
        {/* the answers */}
        {[["A", 250, 262, "#fff", "#14246a"], ["B", 326, 262, "#2ed36a", "#fff"], ["C", 250, 304, "#fff", "#14246a"], ["D", 326, 304, "#fff", "#14246a"]].map(([l, x, y, f, t]) => (
          <g key={l as string} transform={`translate(${x} ${y})`}>
            <rect width={66} height={32} rx={16} fill="#0a1a66" opacity={0.25} transform="translate(0 4)" />
            <rect width={66} height={32} rx={16} fill={f as string} stroke={f === "#2ed36a" ? "#fff" : "#ffd23f"} strokeWidth={3} />
            {f === "#2ed36a" ? <path d="M22 17 l7 7 l14 -15" fill="none" stroke="#fff" strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" /> : <text x={33} y={23} textAnchor="middle" fontSize={18} fill={t as string} style={{ fontFamily: "var(--display)" }}>{l as string}</text>}
          </g>
        ))}
        {/* VS */}
        <g transform="translate(320 190) rotate(-6)">
          <polygon points={Array.from({ length: 24 }, (_, i) => { const a = (i / 24) * Math.PI * 2, r = i % 2 ? 40 : 54; return `${(Math.cos(a) * r).toFixed(1)},${(Math.sin(a) * r).toFixed(1)}`; }).join(" ")} fill="#fff" />
          <polygon points={Array.from({ length: 24 }, (_, i) => { const a = (i / 24) * Math.PI * 2, r = i % 2 ? 36 : 49; return `${(Math.cos(a) * r).toFixed(1)},${(Math.sin(a) * r).toFixed(1)}`; }).join(" ")} fill="#ffc61a" />
          <text y={15} textAnchor="middle" fontSize={44} fill="#c43b00" stroke="#fff" strokeWidth={0} style={{ fontFamily: "var(--display)" }}>VS</text>
        </g>
        <Star5 x={52} y={150} r={13} fill="#fff6bd" stroke="#ffb000" /><Star5 x={590} y={140} r={15} fill="#fff6bd" stroke="#ffb000" />
        <Sparkle x={210} y={148} s={10} /><Sparkle x={436} y={148} s={10} />
      </L2>
    </>
  );
}

/* ─────────────── Vidensræs: two cars on a road in perspective, towards the finish ─────────────── */
function Car({ x, y, s, body, dark }: { x: number; y: number; s: number; body: string; dark: string }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <ellipse cx={0} cy={28} rx={74} ry={11} fill="#000" opacity={0.28} />
      <rect x={-68} y={-2} width={22} height={30} rx={8} fill="#1b1f33" /><rect x={46} y={-2} width={22} height={30} rx={8} fill="#1b1f33" />
      <rect x={-58} y={-22} width={116} height={46} rx={16} fill={dark} />
      <rect x={-58} y={-26} width={116} height={44} rx={16} fill={body} />
      <path d="M-40 -24 L-28 -56 H28 L40 -24Z" fill={dark} />
      <path d="M-32 -28 L-23 -50 H23 L32 -28Z" fill="#a8e8ff" /><path d="M-28 -30 L-21 -46 H-6 L-12 -30Z" fill="#fff" opacity={0.6} />
      <rect x={-56} y={-48} width={112} height={9} rx={4.5} fill={dark} /><rect x={-40} y={-42} width={7} height={18} fill={dark} /><rect x={33} y={-42} width={7} height={18} fill={dark} />
      <rect x={-52} y={-6} width={26} height={13} rx={5} fill="#ff2d3d" /><rect x={26} y={-6} width={26} height={13} rx={5} fill="#ff2d3d" />
      <rect x={-52} y={-6} width={26} height={4} rx={2} fill="#ff9aa2" opacity={0.8} /><rect x={26} y={-6} width={26} height={4} rx={2} fill="#ff9aa2" opacity={0.8} />
      <rect x={-17} y={6} width={34} height={13} rx={4} fill="#fff" /><rect x={-11} y={10} width={22} height={5} rx={2} fill="#c6d4f6" />
      <rect x={-1.5} y={-26} width={3} height={0} fill="#fff" />
    </g>
  );
}
export function Videnraes({ u }: SceneProps) {
  const H = 172, B = 362, rl = (y: number) => 298 + (-70 - 298) * ((y - H) / (B - H)), rr = (y: number) => 342 + (710 - 342) * ((y - H) / (B - H));
  const yy = (t: number) => H + (B - H) * Math.pow(t, 1.9);
  const segs: ReactNode[] = [];
  for (let k = 0; k < 10; k++) {
    const y0 = yy(k / 10), y1 = yy((k + 1) / 10), cw0 = 3 + 38 * ((y0 - H) / (B - H)), cw1 = 3 + 38 * ((y1 - H) / (B - H));
    const red = k % 2 === 0;
    segs.push(<polygon key={`l${k}`} points={pts([[rl(y0) - cw0, y0], [rl(y0), y0], [rl(y1), y1], [rl(y1) - cw1, y1]])} fill={red ? "#ff3b4a" : "#fff"} />);
    segs.push(<polygon key={`r${k}`} points={pts([[rr(y0), y0], [rr(y0) + cw0, y0], [rr(y1) + cw1, y1], [rr(y1), y1]])} fill={red ? "#ff3b4a" : "#fff"} />);
    if (k % 2 === 0) {
      const m0 = (rl(y0) + rr(y0)) / 2, m1 = (rl(y1) + rr(y1)) / 2, d0 = 1 + 7 * ((y0 - H) / (B - H)), d1 = 1 + 7 * ((y1 - H) / (B - H));
      segs.push(<polygon key={`m${k}`} points={pts([[m0 - d0, y0], [m0 + d0, y0], [m1 + d1, y1], [m1 - d1, y1]])} fill="#fff" opacity={0.95} />);
    }
  }
  const checks: ReactNode[] = [];
  for (let r = 0; r < 2; r++) for (let c = 0; c < 20; c++) checks.push(<rect key={`${r}-${c}`} x={248 + c * 7.2} y={104 + r * 7.2} width={7.2} height={7.2} fill={(r + c) % 2 ? "#14246a" : "#fff"} />);
  return (
    <>
      <defs>
        <linearGradient id={`${u}sky`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#3fb5ff" /><stop offset="1" stopColor="#ffe9a8" /></linearGradient>
        <linearGradient id={`${u}gr`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#8fe36a" /><stop offset="1" stopColor="#2faa45" /></linearGradient>
        <linearGradient id={`${u}rd`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#7d86a8" /><stop offset="1" stopColor="#434b6e" /></linearGradient>
      </defs>
      <L0>
        <rect width="640" height="180" fill={`url(#${u}sky)`} />
        <circle cx={320} cy={172} r={92} fill="#fff7c8" opacity={0.7} />
        <Cloud x={110} y={58} s={0.9} /><Cloud x={520} y={78} s={0.75} o={0.85} /><Cloud x={350} y={36} s={0.55} o={0.8} />
        <path d="M0 172 L0 140 Q60 108 130 150 Q190 118 250 160 L250 172Z" fill="#69c97a" />
        <path d="M390 172 L390 156 Q450 118 520 150 Q580 122 640 146 L640 172Z" fill="#59bd72" />
        <rect y={170} width="640" height="190" fill={`url(#${u}gr)`} />
      </L0>
      <L1>
        <polygon points={pts([[298, H], [342, H], [710, B], [-70, B]])} fill={`url(#${u}rd)`} />
        {segs}
        {/* finish arch */}
        <rect x={236} y={104} width={12} height={72} fill="#14246a" /><rect x={392} y={104} width={12} height={72} fill="#14246a" />
        <rect x={244} y={100} width={152} height={26} rx={4} fill="#14246a" />
        {checks}
        <rect x={244} y={100} width={152} height={4} fill="#fff" opacity={0.35} />
        {/* question boxes on the road */}
        {[[300, 214, 22], [350, 250, 28]].map(([x, y, s], i) => (
          <g key={i}>
            <ellipse cx={x} cy={y! + 6} rx={s! * 0.9} ry={s! * 0.28} fill="#000" opacity={0.25} />
            <Cube x={x!} y={y!} s={s!} top="#fff2a0" left="#ffc61a" right="#e8940c" />
            <text x={x} y={y! - s! * 0.18} textAnchor="middle" fontSize={s! * 0.9} fill="#c43b00" style={{ fontFamily: "var(--display)" }}>?</text>
          </g>
        ))}
        <Car x={286} y={292} s={0.78} body="#ff4d6d" dark="#b81f40" />
        <Car x={440} y={336} s={1} body="#2f8dff" dark="#1450b8" />
      </L1>
      <L2>
        <Streaks items={[[20, 220, 80], [50, 270, 60], [560, 230, 70], [590, 296, 40], [-10, 320, 90]]} o={0.55} />
        {[[60, 190, "#ff4d6d"], [580, 180, "#2f8dff"]].map(([x, y, c], i) => (
          <g key={i} transform={`translate(${x} ${y})`}>
            <rect x={-3} y={-60} width={6} height={110} rx={3} fill="#14246a" />
            <g transform="translate(3 -60)">{[0, 1, 2, 3].map((r) => [0, 1, 2, 3].map((q) => <rect key={`${r}${q}`} x={q * 12} y={r * 10} width={12} height={10} fill={(r + q) % 2 ? "#14246a" : "#fff"} />))}</g>
            <circle cy={-64} r={6} fill={c as string} />
          </g>
        ))}
        {[[210, 40, "#ff4d6d"], [430, 52, "#ffd23f"], [90, 108, "#2ed36a"], [540, 120, "#a855f7"], [330, 26, "#2f8dff"], [160, 74, "#ffd23f"], [480, 84, "#ff4d6d"]].map(([x, y, c], i) => <rect key={i} x={x as number} y={y as number} width={9} height={5} rx={1.5} fill={c as string} transform={`rotate(${i * 37} ${x} ${y})`} />)}
        <Star5 x={586} y={46} r={14} fill="#fff6bd" stroke="#ffb000" /><Sparkle x={52} y={52} s={12} />
      </L2>
    </>
  );
}

/* ─────────────── Tårnforsvar: a winding path, balloons and towers that pop them ─────────────── */
function Balloon({ x, y, c, d, s = 1 }: { x: number; y: number; c: string; d: string; s?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <path d="M0 24 q-5 10 3 18 q6 6 0 14" fill="none" stroke="#fff" strokeOpacity={0.8} strokeWidth={2} />
      <ellipse cx={2} cy={4} rx={19} ry={23} fill={d} />
      <path d="M0 -20 C-20 -20 -20 20 0 24 C20 20 20 -20 0 -20Z" fill={c} />
      <path d="M-4 24 l4 -3 l4 3Z" fill={d} />
      <ellipse cx={-7} cy={-8} rx={4.5} ry={8} fill="#fff" opacity={0.6} transform="rotate(14 -7 -8)" />
    </g>
  );
}
function Tower({ x, y, rot, c, d, star }: { x: number; y: number; rot: number; c: string; d: string; star?: boolean }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse cx={3} cy={26} rx={38} ry={13} fill="#000" opacity={0.2} />
      <path d="M-30 24 L-22 -6 H22 L30 24Z" fill="#9aa6c8" /><path d="M-30 24 L-22 -6 H-6 L-10 24Z" fill="#c1caE2" opacity={0.7} />
      <rect x={-34} y={20} width={68} height={10} rx={5} fill="#6f7ca3" />
      <g transform={`translate(0 -10) rotate(${rot})`}>
        <rect x={0} y={-8} width={44} height={16} rx={6} fill={d} /><rect x={36} y={-10} width={12} height={20} rx={4} fill="#3a4666" />
        <circle r={22} fill={d} /><circle r={19} fill={c} />
        {star ? <Star5 x={0} y={0} r={11} fill="#fff6bd" stroke="#e08e00" sw={1.5} /> : <circle r={8} fill="#fff" opacity={0.85} />}
        <path d="M-12 -10 A15 15 0 0 1 0 -17" fill="none" stroke="#fff" strokeOpacity={0.8} strokeWidth={3} strokeLinecap="round" />
      </g>
    </g>
  );
}
export function Taarn({ u }: SceneProps) {
  const path = "M-30 92 H200 V252 H440 V112 H680";
  return (
    <>
      <Bg u={u} a="#8bea7a" b="#2fa850" glow={0.55} cy={30} />
      <L0>
        <Rays cx={320} cy={-20} n={16} len={620} opacity={0.12} />
        {[[40, 300], [596, 304], [560, 30], [90, 40], [356, 328]].map(([x, y], i) => (
          <g key={i} transform={`translate(${x} ${y})`}>
            <ellipse cx={4} cy={14} rx={26} ry={9} fill="#000" opacity={0.12} />
            <circle cy={-4} r={i % 2 ? 22 : 28} fill="#1f9d4a" /><circle cx={-6} cy={-12} r={i % 2 ? 14 : 18} fill="#37c065" />
          </g>
        ))}
        {[[260, 300, "#ff6f91"], [390, 60, "#fff"], [120, 328, "#ffd23f"], [600, 200, "#fff"], [60, 190, "#ff6f91"]].map(([x, y, c], i) => (
          <g key={i} transform={`translate(${x} ${y})`}><circle r={4} fill={c as string} /><circle cx={7} cy={-3} r={3} fill={c as string} /><circle cx={-6} cy={-4} r={3} fill={c as string} /><circle cx={0} cy={-1} r={2} fill="#ffd23f" /></g>
        ))}
      </L0>
      <L1>
        <path d={path} fill="none" stroke="#b98a3e" strokeWidth={68} strokeLinejoin="round" />
        <path d={path} fill="none" stroke="#f2d79a" strokeWidth={58} strokeLinejoin="round" />
        <path d={path} fill="none" stroke="#fff0c8" strokeWidth={4} strokeDasharray="2 16" strokeLinecap="round" opacity={0.9} />
        <Tower x={118} y={196} rot={-52} c="#58c7f0" d="#1d84b8" />
        <Tower x={322} y={156} rot={28} c="#ffb347" d="#d97a00" star />
        <Tower x={540} y={236} rot={-120} c="#ff8aa0" d="#c42040" />
        {/* shots */}
        <g fill="#fff2a0" stroke="#e0a400" strokeWidth={2}>
          <circle cx={168} cy={156} r={7} /><circle cx={350} cy={196} r={7} /><circle cx={500} cy={200} r={7} />
        </g>
        <Balloon x={86} y={92} c="#ff4d6d" d="#b81f40" /><Balloon x={146} y={90} c="#2f8dff" d="#1450b8" s={0.9} /><Balloon x={206} y={142} c="#ffd23f" d="#d9a000" s={1.05} />
        <Balloon x={290} y={252} c="#2ed36a" d="#15803d" /><Balloon x={460} y={186} c="#a855f7" d="#6b21a8" s={0.95} /><Balloon x={440} y={118} c="#ff4d6d" d="#b81f40" s={0.9} />
      </L1>
      <L2>
        {/* pop! */}
        <g transform="translate(236 214)">
          <polygon points="0,-34 8,-14 28,-22 18,-4 38,6 16,12 22,32 4,20 -8,36 -12,16 -32,22 -22,4 -38,-8 -16,-12 -20,-30 -4,-18" fill="#fff6bd" stroke="#ff8a1f" strokeWidth={3} strokeLinejoin="round" />
        </g>
        <Coin x={236} y={214} r={13} />
        <Coin x={534} y={116} r={15} rot={14} /><Coin x={80} y={300} r={13} rot={-12} />
        <g transform="translate(578 52)"><path d="M0 14 C-26 -4 -16 -26 0 -12 C16 -26 26 -4 0 14Z" fill="#ff4d6d" stroke="#fff" strokeWidth={3} strokeLinejoin="round" /></g>
        <Sparkle x={420} y={40} s={11} /><Sparkle x={40} y={140} s={9} />
      </L2>
    </>
  );
}

/* ─────────────── Skattejagt: an open treasure chest on a sunset island ─────────────── */
export function Skattejagt({ u }: SceneProps) {
  const feet: ReactNode[] = [[70, 342], [98, 332], [124, 336], [152, 326], [176, 328]].map(([x, y], i) => (
    <g key={i} transform={`translate(${x} ${y}) rotate(${-18 + (i % 2) * 30})`}><ellipse rx={5} ry={8} fill="#c58f3a" opacity={0.65} /><ellipse cy={-10} rx={3.5} ry={4} fill="#c58f3a" opacity={0.65} /></g>
  ));
  return (
    <>
      <defs>
        <linearGradient id={`${u}sk`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#5a3fc0" /><stop offset="0.5" stopColor="#ff6f8a" /><stop offset="1" stopColor="#ffd27a" /></linearGradient>
        <linearGradient id={`${u}sea`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#36b3e8" /><stop offset="1" stopColor="#1b56b8" /></linearGradient>
        <linearGradient id={`${u}sand`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#ffe5a0" /><stop offset="1" stopColor="#f2b95e" /></linearGradient>
      </defs>
      <L0>
        <rect width="640" height="200" fill={`url(#${u}sk)`} />
        <circle cx={460} cy={196} r={150} fill="#fff" opacity={0.12} /><circle cx={460} cy={196} r={96} fill="#fff" opacity={0.2} />
        <circle cx={460} cy={196} r={60} fill="#fff1a8" />
        <rect y={196} width="640" height="164" fill={`url(#${u}sea)`} />
        <path d="M400 200 H520 M410 210 H510 M424 222 H496" stroke="#fff3b0" strokeWidth={4} strokeLinecap="round" opacity={0.6} />
        <Cloud x={120} y={60} s={0.8} o={0.35} /><Cloud x={560} y={44} s={0.6} o={0.3} />
        {[[40, 28], [200, 30], [300, 70], [590, 100]].map(([x, y], i) => <Sparkle key={i} x={x!} y={y!} s={i % 2 ? 6 : 9} o={0.8} />)}
        <g transform="translate(560 198)"><path d="M0 0 L-14 -40 L-2 -36 L-2 -46 L8 -34 L16 0Z" fill="#19205e" opacity={0.0} /></g>
      </L0>
      <L1>
        <ellipse cx={320} cy={312} rx={400} ry={92} fill="#e6a94a" />
        <ellipse cx={320} cy={304} rx={392} ry={86} fill={`url(#${u}sand)`} />
        <ellipse cx={320} cy={314} rx={200} ry={40} fill="#fff" opacity={0.16} />
        {feet}
        {/* X marks the spot */}
        <g transform="translate(206 328)" stroke="#d6263f" strokeWidth={11} strokeLinecap="round"><path d="M-14 -10 L14 10 M14 -10 L-14 10" /></g>
        {/* palm trees */}
        {[[96, 262, 1], [566, 268, 0.78]].map(([x, y, k], i) => (
          <g key={i} transform={`translate(${x} ${y}) scale(${k! * (i ? -1 : 1)} ${k})`}>
            <ellipse cx={8} cy={8} rx={30} ry={8} fill="#000" opacity={0.15} />
            <path d="M0 4 Q10 -50 -4 -104 L6 -106 Q22 -50 14 4Z" fill="#9a6a2e" />
            {[-8, -26, -44, -62, -80].map((yy, j) => <path key={j} d={`M${-1 + j * -0.2} ${yy} q8 -3 16 0`} stroke="#6b4519" strokeWidth={2} fill="none" />)}
            <g transform="translate(0 -106)">
              {[[-70, 14, -24], [70, 10, 24], [-48, -26, -52], [46, -28, 50], [0, -46, 0]].map(([dx, dy, r], j) => (
                <g key={j} transform={`rotate(${r})`}><path d={`M0 0 Q${dx! * 0.5} ${dy! - 30} ${dx} ${dy! + 22} Q${dx! * 0.4} ${dy! + 6} 0 8Z`} fill={j % 2 ? "#23a85a" : "#32c870"} /></g>
              ))}
              <circle cx={-6} cy={6} r={7} fill="#7a4a1a" /><circle cx={7} cy={8} r={7} fill="#8a5a22" />
            </g>
          </g>
        ))}
      </L1>
      <L2>
        {/* light from the chest */}
        <Rays cx={320} cy={238} n={14} len={260} color="#fff6bd" opacity={0.28} spread={0.07} rot={-Math.PI / 2 + 0.2} />
        <g transform="translate(320 306) scale(1.3)">
          <ellipse cx={4} cy={10} rx={96} ry={16} fill="#000" opacity={0.2} />
          <path d="M-68 -66 L-62 -138 Q0 -156 62 -138 L68 -66Z" fill="#5a2f12" />
          <path d="M-68 -66 L-62 -138 Q0 -156 62 -138 L68 -66 Q0 -84 -68 -66Z" fill="#7a4018" />
          <rect x={-66} y={-120} width={132} height={9} fill="#ffc61a" opacity={0.9} />
          <ellipse cx={0} cy={-64} rx={70} ry={16} fill="#ffe27a" />
          <path d="M-62 -64 Q-40 -108 0 -112 Q40 -108 62 -64Z" fill="#ffc61a" stroke="#d98200" strokeWidth={2.5} />
          {[[-34, -80], [-6, -98], [22, -84], [44, -70], [-48, -68], [4, -76], [-20, -70]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={9} fill={i % 2 ? "#ffd84a" : "#ffe9a0"} stroke="#d98200" strokeWidth={2} />)}
          <Gem x={-18} y={-106} s={13} a="#ff9ac2" b="#e0407f" /><Gem x={24} y={-100} s={11} a="#9ff0ff" b="#1f9bd6" />
          <path d="M-72 -62 H72 V0 Q72 10 62 10 H-62 Q-72 10 -72 0Z" fill="#9a5a26" />
          <path d="M-72 -62 H72 V-50 H-72Z" fill="#b8702e" />
          {[-34, -12, 12, 34].map((x) => <line key={x} x1={x} y1={-50} x2={x} y2={8} stroke="#7a4018" strokeWidth={2} />)}
          <rect x={-70} y={-62} width={15} height={72} rx={3} fill="#ffc61a" stroke="#d98200" strokeWidth={2.5} /><rect x={55} y={-62} width={15} height={72} rx={3} fill="#ffc61a" stroke="#d98200" strokeWidth={2.5} />
          <rect x={-14} y={-72} width={28} height={34} rx={6} fill="#ffc61a" stroke="#d98200" strokeWidth={3} />
          <circle cy={-58} r={5} fill="#5a2f12" /><rect x={-2} y={-58} width={4} height={12} rx={2} fill="#5a2f12" />
          <path d="M-58 -58 H-30" stroke="#fff" strokeOpacity={0.4} strokeWidth={4} strokeLinecap="round" />
        </g>
        {/* the key */}
        <g transform="translate(528 318) rotate(-28)">
          <circle r={19} fill="#d98200" cy={2} /><circle r={19} fill="#ffc61a" stroke="#d98200" strokeWidth={3} /><circle r={8} fill="#c9a24a" />
          <rect x={16} y={-6} width={62} height={12} rx={5} fill="#ffc61a" stroke="#d98200" strokeWidth={3} />
          <rect x={56} y={4} width={9} height={16} fill="#ffc61a" stroke="#d98200" strokeWidth={2.5} /><rect x={69} y={4} width={9} height={11} fill="#ffc61a" stroke="#d98200" strokeWidth={2.5} />
        </g>
        <CoinStack x={84} y={330} n={4} r={20} /><Coin x={440} y={340} r={14} rot={20} />
        <Sparkle x={250} y={176} s={14} /><Sparkle x={394} y={170} s={11} /><Sparkle x={320} y={130} s={9} fill="#fff6bd" />
      </L2>
    </>
  );
}
